/**
 * "Full details" — the one control that opens a reopened week's story, shared
 * by the week row and the rollup detail page.
 *
 * ONE component on purpose. Both surfaces state the same headline and owe the
 * PM the same four facts; two implementations would be two places for the copy
 * and the affordance to drift, and the difference between them is a single
 * argument (which artifact each can point at).
 *
 * It IS a `HelpTip` — the app's tooltip vocabulary — rather than a new popover:
 * the portal-to-body positioning, the flip-when-clipped, the hover-with-delay,
 * the click-to-pin, Escape and outside-click dismissal are all solved there,
 * and a second implementation would have to re-solve the clipping problem the
 * portal exists for (this banner lives inside `.week-table`, which owns an
 * `overflow-x: auto`). The only thing added for this was the labeled trigger
 * variant, since a bare question mark could not carry Brandon's "Full Details
 * button".
 */
import { HelpTip } from './HelpTip';
import {
  revisionDetail,
  REVISION_DETAILS_LABEL,
  type RevisionBannerSurface,
} from '../lib/revisionBanner';
import type { RollupRevisionContext } from '@coffman-tm/shared';

export function RevisionDetailsTip({
  context,
  currentInvoiceTotalCents,
  gcLabel,
  surface = 'week-row',
}: {
  /** What landed since the last send. Absent on an API older than the field —
   *  the popover degrades to the send-agnostic copy rather than disappearing,
   *  because the money consequence is true either way. */
  context: RollupRevisionContext | undefined;
  currentInvoiceTotalCents: number;
  /** Settings-sourced GC name from the caller (`gcName`) — never a literal. */
  gcLabel: string;
  surface?: RevisionBannerSurface;
}) {
  const detail = revisionDetail(context, currentInvoiceTotalCents, gcLabel, surface);
  return (
    <HelpTip label={REVISION_DETAILS_LABEL} learnMore="weekly-rollups">
      <span style={{ display: 'block' }}>{detail.sentLine}</span>
      {detail.reportsHeading && (
        <>
          <span style={{ display: 'block', marginTop: 6 }}>{detail.reportsHeading}</span>
          <span style={{ display: 'block', marginTop: 2 }}>
            {detail.reportLines.map((line, i) => (
              // Own line each: the popover is a block, so a comma-run would
              // undo the reason the list moved out of the row in the first
              // place.
              //
              // Keyed by INDEX, not by the text: two reports can share a
              // foreman and a report date (`selectLatePdfs` de-duplicates by
              // PDF id, not by how the line reads), which makes the string a
              // colliding key. The list is static and never reorders, so the
              // index is stable for exactly as long as it needs to be.
              <span key={i} style={{ display: 'block', paddingLeft: 8 }}>
                {line}
              </span>
            ))}
          </span>
        </>
      )}
      <span style={{ display: 'block', marginTop: 6 }}>{detail.closing}</span>
    </HelpTip>
  );
}
